import { CheckCircle2, Clock, MapPin, Phone } from "lucide-react";

/**
 * Product mockup for the hero section.
 *
 * This is a CSS/JSX-only representation of the app's actual calendar +
 * invoice preview — no images, no external assets. It reads as "real
 * product" because:
 *   - Uses the same tinted booking blocks the real Day/Week views use.
 *   - Floats an "invoice paid" card and a "staff on-site" chip as depth
 *     layers with subtle shadow + backdrop blur.
 *   - All copy is domain-authentic (booking codes, payment types).
 *
 * Kept intentionally static (no live state) — this is a marketing
 * illustration, not a live demo.
 */
export default function HeroMockup() {
  const days = ["MON", "TUE", "WED", "THU", "FRI"];
  // Deterministic booking layout per weekday for a legible mock.
  const grid = [
    [{ h: 44, tone: "amber",  label: "09:00 Murphy",  sub: "Pump hire · €250" },
     { h: 32, tone: "emerald",label: "13:00 Kilkenny",sub: "Boiler service" }],
    [{ h: 60, tone: "amber",  label: "10:30 O'Brien", sub: "Piping install · €480" }],
    [{ h: 36, tone: "sky",    label: "08:00 Foxford", sub: "Cash · €180" },
     { h: 52, tone: "amber",  label: "12:00 Ryan",    sub: "VAT invoice" },
     { h: 28, tone: "rose",   label: "16:30 Doyle",   sub: "Follow-up" }],
    [{ h: 40, tone: "emerald",label: "09:30 Byrne",   sub: "RCT · €620" },
     { h: 44, tone: "amber",  label: "14:00 Casey",   sub: "Pump swap" }],
    [{ h: 68, tone: "amber",  label: "10:00 Kelly",   sub: "Full day · €890" }],
  ];

  const toneMap = {
    amber:   "bg-accent/90 border-accent",
    emerald: "bg-emerald-400/80 border-emerald-300",
    sky:     "bg-sky-400/80 border-sky-300",
    rose:    "bg-rose-400/80 border-rose-300",
  };

  return (
    <div className="relative" aria-hidden="true" data-testid="hero-mockup">
      {/* Frame: light card standing on dark hero */}
      <div className="relative rounded-2xl border border-primary-foreground/10 bg-card text-card-foreground shadow-[0_30px_80px_-20px_rgba(0,0,0,0.5)] overflow-hidden">
        {/* Faux window chrome */}
        <div className="flex items-center gap-1.5 px-4 py-2.5 bg-secondary/60 border-b border-border">
          <span className="h-2.5 w-2.5 rounded-full bg-rose-400/70" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
          <div className="ml-3 mono text-[10px] uppercase tracking-widest text-muted-foreground">
            jobsbook.app / calendar / week
          </div>
        </div>

        <div className="grid grid-cols-5 gap-2 p-4 md:p-5">
          {days.map((d, di) => (
            <div key={d} className="min-w-0">
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground text-center mb-2">
                {d}
              </div>
              <div className="space-y-1.5">
                {grid[di].map((b, i) => (
                  <div
                    key={i}
                    className={`rounded-md border-l-[3px] ${toneMap[b.tone]} px-2 py-1.5 text-[10px] leading-tight font-bold text-white`}
                    style={{ height: `${b.h}px` }}
                  >
                    <div className="truncate">{b.label}</div>
                    <div className="opacity-80 font-medium text-[9px] mt-0.5 truncate">
                      {b.sub}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Floating invoice card — bottom-left, offset outward */}
      <div className="hidden sm:block absolute -bottom-6 -left-6 w-[220px] bg-card border border-border rounded-xl shadow-2xl p-3.5 rotate-[-3deg]">
        <div className="flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest text-emerald-700">
          <CheckCircle2 className="h-3 w-3" /> paid
        </div>
        <div className="font-black text-sm mt-1 truncate">Foxford Civils</div>
        <div className="mono text-[11px] text-muted-foreground mt-0.5">I-000147</div>
        <div className="flex items-baseline justify-between mt-2 pt-2 border-t border-border">
          <span className="text-xs text-muted-foreground">Total</span>
          <span className="font-black text-lg">€482.00</span>
        </div>
      </div>

      {/* Floating "on site" chip — top-right */}
      <div className="hidden sm:flex absolute -top-4 -right-4 items-center gap-2 bg-primary text-primary-foreground border border-primary-foreground/10 rounded-full px-3.5 py-2 shadow-xl">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
        </span>
        <span className="mono text-[10px] uppercase tracking-widest">John · on site</span>
      </div>

      {/* Floating "staff card" — right side, lower */}
      <div className="hidden lg:block absolute top-1/2 -right-8 w-[190px] bg-card border border-border rounded-xl shadow-2xl p-3 rotate-[2deg]">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-accent grid place-items-center text-accent-foreground font-black text-xs">
            JD
          </div>
          <div className="min-w-0">
            <div className="font-black text-xs truncate">John Doyle</div>
            <div className="mono text-[10px] text-muted-foreground">/s/john-x7k2</div>
          </div>
        </div>
        <div className="mt-2.5 space-y-1 text-[10px]">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <MapPin className="h-3 w-3 shrink-0" />
            <span className="truncate">Fermoy → Mallow</span>
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Clock className="h-3 w-3 shrink-0" />
            <span>3 jobs · 6h 40m</span>
          </div>
          <div className="flex items-center gap-1.5 text-emerald-700 font-bold">
            <Phone className="h-3 w-3 shrink-0" />
            <span>Cash collected: €420</span>
          </div>
        </div>
      </div>
    </div>
  );
}
